import { useMemo, useState, type ReactNode } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GlassCard } from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { TrendChart } from '@/versions/v1/modules/_viz';
import { ActorChip } from '@/versions/v1/modules/advisory/PipelineStrip';
import { SynChip } from '@/versions/v1/modules/ingestion/SynChip';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { useOperatorRounds } from '@/versions/v1/shared/OperatorRoundProvider';
import { projectSidebarHref } from '@/versions/v1/shared/project-sidebar-nav';
import { LandingStripTabs } from './GmLeadBand';
import { GmCard, statePillClass } from './GmTabs';
import {
  operatorAlerts,
  operatorAreaRows,
  operatorContacts,
  operatorGuidance,
  operatorLoopsCard,
  operatorNextForMe,
  operatorPhTrace,
  operatorRecordSubmit,
  operatorRoundCaption,
  operatorRoundSheet,
  operatorStripCells,
  operatorSubtitle,
  operatorTasks,
  type OperatorAreaRow,
  type OperatorRun,
  type OperatorTabId,
  type OperatorTask,
} from './OperatorLanding.transform';

type OpenCase = (caseId: string) => void;

// ---- Primitives on the comp's recipe ------------------------------------------------

const RUN_TONE: Record<'ok' | 'warn' | 'bad', string> = {
  ok: 'text-[var(--state-online)]',
  warn: 'text-[var(--state-warning)]',
  bad: 'text-[var(--state-blocked)]',
};

/** A line of runs: the lead term in 600 ink-secondary, an abnormal reading in its state tone. */
function Runs({ runs, strongClass = 'font-semibold text-ink-secondary' }: { runs: readonly OperatorRun[]; strongClass?: string }) {
  return (
    <>
      {runs.map((run, index) => (
        <span key={index} className={cn(run.strong && strongClass, run.tone && RUN_TONE[run.tone], run.tone && run.strong && 'font-semibold')}>
          {run.text}
        </span>
      ))}
    </>
  );
}

const CAPS = 'text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted';
const GO_LINK =
  'inline-flex items-center gap-1 whitespace-nowrap border-b border-[var(--hairline-strong)] text-[12.5px] font-semibold text-ink-primary hover:border-ink-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]';
const PRIMARY_BUTTON =
  'inline-flex h-7 items-center whitespace-nowrap rounded-[10px] bg-ink-primary px-3 text-[12.5px] font-semibold text-[var(--surface-2)] transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)] disabled:cursor-not-allowed disabled:opacity-40';

/** The task state glyph (comp `.gy`): the state is the glyph's, never the category's (adjudication 6). */
const GLYPH: Record<OperatorTask['state'], { mark: string; className: string; label: string }> = {
  done: { mark: '✓', className: 'text-[var(--state-online)]', label: 'done' },
  'in-progress': { mark: '◐', className: 'text-[var(--state-info)]', label: 'in progress' },
  overdue: { mark: '!', className: 'font-bold text-[var(--state-blocked)]', label: 'overdue' },
  due: { mark: '○', className: 'text-muted', label: 'due' },
};

const WHEN_TONE: Record<OperatorTask['state'], string> = {
  done: 'text-muted',
  'in-progress': 'font-semibold text-[var(--state-info)]',
  overdue: 'font-semibold text-[var(--state-blocked)]',
  due: 'text-muted',
};

// ---- The lead band ----------------------------------------------------------------

function LoopsCard({ href }: { href: string }) {
  const card = operatorLoopsCard();
  return (
    <Link
      to={href}
      data-operator-loops
      aria-label={`Loops · ${card.manual} in manual · Process`}
      className="col-span-12 flex min-h-[7.5rem] min-w-0 flex-col gap-1 rounded-2xl border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-4 py-3 transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)] lg:col-span-3"
    >
      <span className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">Loops</span>
      <span className="flex flex-wrap items-baseline gap-x-1.5 text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary">
        <span data-operator-loops-figure>{card.manual}</span>
        <span className="text-xs font-semibold tracking-normal text-muted">in manual</span>
      </span>
      <span className="text-xs leading-snug tabular-nums text-muted" data-operator-loops-comparison>
        <span className="font-semibold text-ink-secondary">{`of ${card.total} loops`}</span>
        {card.tag ? ` · ${card.tag}` : ''}
        {card.since ? ` · since ${card.since}` : ''}
      </span>
      <span className="mt-auto inline-flex items-center gap-1 pt-1 text-[12.5px] font-semibold text-ink-primary">
        Process
        <ArrowUpRight className="size-3.5" aria-hidden="true" />
      </span>
    </Link>
  );
}

function OperatorLeadBand({
  state,
  tab,
  onTabChange,
  processHref,
}: {
  state: SessionState;
  tab: OperatorTabId;
  onTabChange: (tab: OperatorTabId) => void;
  processHref: string;
}) {
  const advisory = useAdvisory();
  const { rounds } = useOperatorRounds();
  return (
    <Commentable name="Kpis">
      <div className="grid grid-cols-12 gap-3" data-operator-lead-band>
        <GlassCard
          as="nav"
          tone="matte"
          aria-label="Landing views"
          className="col-span-12 overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0 lg:col-span-9"
        >
          <LandingStripTabs
            cells={operatorStripCells(state, advisory, rounds)}
            selected={tab}
            onSelect={onTabChange}
            dataPrefix="op"
            panelId="operator-landing-panel"
            columnsClassName="sm:grid-cols-2 lg:grid-cols-4"
          />
        </GlassCard>
        <LoopsCard href={processHref} />
      </div>
    </Commentable>
  );
}

// ---- Area status ------------------------------------------------------------------

/** The area read's five columns, set once on the list; each row is a subgrid,
 *  so the Against column sizes to its widest reading and every row aligns. */
const AREA_COLUMNS = 'grid grid-cols-[72px_96px_minmax(0,1fr)_minmax(0,1.5fr)_14px] gap-x-2 min-[1400px]:grid-cols-[88px_112px_auto_minmax(0,1fr)_14px] min-[1400px]:gap-x-3';
const AREA_GRID = 'col-span-5 grid grid-cols-subgrid';

function AreaRow({ row, href }: { row: OperatorAreaRow; href: string }) {
  return (
    <Link
      to={`${href}?node=${row.id}`}
      data-operator-strip-cell={row.id}
      data-operator-area-off={row.offBand ? 'true' : undefined}
      aria-label={`${row.name} · open in Plant model`}
      className={cn(
        AREA_GRID,
        'items-baseline border-b border-[var(--color-divider)] px-4 py-[7px] text-[13px] transition-colors last:border-b-0 hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-500)]',
        row.offBand &&
          'bg-[color-mix(in_srgb,var(--state-warning)_11%,transparent)] shadow-[inset_3px_0_0_var(--state-warning)] hover:bg-[color-mix(in_srgb,var(--state-warning)_15%,transparent)]',
      )}
    >
      <span className={cn('whitespace-nowrap', CAPS, 'text-[11px]')}>{row.name}</span>
      <span className="whitespace-nowrap font-semibold tabular-nums text-ink-primary" data-operator-area-figure={row.id}>
        {row.figure}
        {row.unit ? <small className="ml-1 text-[11.5px] font-medium text-muted">{row.unit}</small> : null}
      </span>
      <span className="text-xs leading-snug tabular-nums text-muted min-[1400px]:whitespace-nowrap" data-operator-area-against={row.id}>
        {row.against ? <Runs runs={row.against} /> : null}
      </span>
      <span className="text-xs leading-snug tabular-nums text-muted [text-wrap:pretty]" data-operator-area-also={row.id}>
        <Runs runs={row.also} />
        {row.chip ? (
          <span className="ml-1.5 inline-flex whitespace-nowrap rounded-full border border-[var(--hairline-strong)] px-1.5 text-[10px] font-semibold uppercase leading-4 tracking-[0.05em] text-ink-secondary">
            {row.chip}
          </span>
        ) : null}
      </span>
      <ArrowUpRight className="size-3 self-center text-muted" aria-hidden="true" />
    </Link>
  );
}

function PhTrace({ state, plantHref }: { state: SessionState; plantHref: string }) {
  const trace = operatorPhTrace(state);
  if (!trace) return null;
  return (
    <div
      className="grid grid-cols-1 gap-x-[18px] gap-y-1.5 border-t border-[var(--hairline)] px-4 py-3 sm:grid-cols-[240px_minmax(0,1fr)] min-[1400px]:grid-cols-[260px_minmax(0,1fr)]"
      data-operator-ph-trace
    >
      <div title={trace.provenance}>
        <TrendChart
          height={84}
          categories={trace.categories}
          series={[{ name: `Zn cell pH ${trace.tag}`, points: trace.points, tone: 'neutral', color: 'var(--ink-secondary)' }]}
          references={[{ value: trace.sp, label: `SP ${trace.sp.toFixed(1)}`, tone: 'neutral', color: 'var(--ink-tertiary)' }]}
          bands={[trace.band]}
          pointTone={(value) => (value < trace.band.lo || value > trace.band.hi ? 'warn' : undefined)}
          yDomain={trace.domain}
          tickCount={3}
        />
      </div>
      <div className="min-w-0 text-xs leading-snug text-ink-secondary">
        <p className="flex flex-wrap items-center gap-2 text-[12.5px] font-semibold text-ink-primary">
          {`Zn cell pH · ${trace.tag} · ${trace.window}`}
          {trace.synthetic ? <SynChip title={trace.provenance} /> : null}
        </p>
        <p className="mt-1 [text-wrap:pretty]" data-operator-ph-read>
          {trace.outSince ? (
            <span className="font-semibold text-[var(--state-warning)]">{`Below band since ${trace.outSince}`}</span>
          ) : (
            <span className="font-semibold text-ink-primary">In band</span>
          )}
          {` · SP ${trace.sp.toFixed(1)} dashed`}
          {trace.raised ? `, raised from ${trace.raised.from.toFixed(1)} at ${trace.raised.at}${trace.raised.by ? ` (${trace.raised.by} lime step)` : ''}` : ''}
          {` · Control Plan band ${trace.band.lo.toFixed(1)}–${trace.band.hi.toFixed(1)} shaded`}
        </p>
        {trace.good ? (
          <p className="mt-1 [text-wrap:pretty]">
            <span className="font-semibold text-[var(--state-online)]">Good looks like</span>
            {` · ${trace.good.text}`}
            {trace.good.tags.length > 0 ? <span className="text-muted">{` (${trace.good.tags.join(', ')})`}</span> : null}
          </p>
        ) : null}
        <p className="mt-1.5">
          <Link to={`${plantHref}?node=sp-zn&reading=rd-kpv-zn-ph`} className={GO_LINK}>
            Zn flotation in Plant model
            <ArrowUpRight className="size-3" aria-hidden="true" />
          </Link>
        </p>
      </div>
    </div>
  );
}

function CircuitCard({ state, plantHref }: { state: SessionState; plantHref: string }) {
  const rows = useMemo(() => operatorAreaRows(state), [state]);
  return (
    <GmCard flush title="Circuit now" meta="plant order" className="h-full" data-operator-circuit>
      <Commentable name="OperatorAreaRead">
        <div role="list" aria-label="Areas in plant order" className={AREA_COLUMNS} data-operator-area-read>
          <div className={cn(AREA_GRID, 'border-b border-[var(--color-divider)] px-4 pb-1.5 pt-2', CAPS)} aria-hidden="true">
            <span>Area</span>
            <span>Reading</span>
            <span>Against</span>
            <span>Also</span>
            <span />
          </div>
          {rows.map((row) => (
            <AreaRow key={row.id} row={row} href={plantHref} />
          ))}
        </div>
      </Commentable>
      <PhTrace state={state} plantHref={plantHref} />
    </GmCard>
  );
}

function TaskDetail({ task }: { task: OperatorTask }) {
  const parts: ReactNode[] = [];
  if (task.response) {
    parts.push(<ActorChip key="resp" actor="Engine">{task.response.code}</ActorChip>);
    parts.push(<span key="resp-at">{`immediate response ${task.response.at}`}</span>);
  }
  if (task.ppe) parts.push(<span key="ppe" className={statePillClass('bad')}>{task.ppe}</span>);
  if (task.ask) {
    parts.push(<ActorChip key="ask" actor="Engine">{`${task.ask.code} ask`}</ActorChip>);
    parts.push(<span key="ask-text">{task.ask.text}</span>);
  }
  if (task.missed) parts.push(<span key="miss">{task.missed}</span>);
  if (task.record) {
    parts.push(
      <span key="rec">
        <span className="font-semibold text-ink-secondary">{`${task.record.readings} readings`}</span>
        {` · ${task.record.lastRound}`}
      </span>,
    );
  }
  if (parts.length === 0) return null;
  return <div className="mt-[3px] flex flex-wrap items-center gap-1.5 text-xs text-muted">{parts}</div>;
}

function NextRow({ task, onRecord }: { task: OperatorTask; onRecord: () => void }) {
  const glyph = GLYPH[task.state];
  return (
    <div
      className="grid grid-cols-[22px_minmax(0,1fr)_auto] items-start gap-x-2.5 gap-y-0.5 border-b border-[var(--color-divider)] py-[9px] last:border-b-0"
      data-operator-next={task.id}
      data-operator-task-state={task.state}
    >
      <span className={cn('text-center text-sm leading-[18px]', glyph.className)} aria-label={glyph.label}>
        {glyph.mark}
      </span>
      <div className="min-w-0">
        <p className="text-[13px] font-semibold leading-snug text-ink-primary">{task.text}</p>
        <TaskDetail task={task} />
      </div>
      <span className={cn('whitespace-nowrap pt-px text-right text-[11.5px] tabular-nums', WHEN_TONE[task.state])}>{task.when}</span>
      {task.record ? (
        <div className="col-start-2 col-end-4 mt-1.5">
          <button type="button" className={PRIMARY_BUTTON} onClick={onRecord} data-operator-record-button>
            Record &amp; submit
          </button>
        </div>
      ) : null}
    </div>
  );
}

function NextForMeCard({ tasks, onGoto }: { tasks: readonly OperatorTask[]; onGoto: (tab: OperatorTabId) => void }) {
  const next = operatorNextForMe(tasks);
  return (
    <GmCard flush title="Next for me" meta="in the order to do them" className="h-full" data-operator-next-for-me>
      <div className="px-4">
        {next.rows.map((task) => (
          <NextRow key={task.id} task={task} onRecord={() => onGoto('round')} />
        ))}
      </div>
      <div className="mx-4 flex items-center justify-between border-t border-[var(--hairline-strong)] py-2.5 text-xs text-muted">
        <span data-operator-next-more>{next.more > 0 ? `${next.more} more due` : ''}</span>
        <button type="button" className={GO_LINK} onClick={() => onGoto('round')}>
          Round sheet
        </button>
      </div>
    </GmCard>
  );
}

function AreaView({
  state,
  tasks,
  plantHref,
  onGoto,
}: {
  state: SessionState;
  tasks: readonly OperatorTask[];
  plantHref: string;
  onGoto: (tab: OperatorTabId) => void;
}) {
  return (
    <div className="grid grid-cols-12 gap-3">
      <Commentable name="Primary">
        <div className="col-span-12 lg:col-span-7">
          <CircuitCard state={state} plantHref={plantHref} />
        </div>
      </Commentable>
      <Commentable name="Secondary">
        <div className="col-span-12 lg:col-span-5">
          <NextForMeCard tasks={tasks} onGoto={onGoto} />
        </div>
      </Commentable>
    </div>
  );
}

// ---- Round sheet ------------------------------------------------------------------

function RoundSheetCard({ state, tasks, caption }: { state: SessionState; tasks: readonly OperatorTask[]; caption: string }) {
  const groups = operatorRoundSheet(state, tasks);
  return (
    <GmCard title={caption} meta="by area, plant order" className="h-full" data-operator-round-sheet>
      {groups.map((group) => (
        <div key={group.id} data-operator-round-group={group.id}>
          <div
            className={cn(
              'grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-2 border-b border-[var(--color-divider)] pb-[5px] pt-[9px]',
            )}
          >
            <span className={cn(CAPS, 'text-[11px]', group.offBand && 'text-[var(--state-warning)]')}>{group.name}</span>
            <span className="text-right text-xs tabular-nums text-muted" data-operator-round-header={group.id}>
              <Runs runs={group.header} strongClass="font-semibold text-ink-primary" />
            </span>
          </div>
          {group.tasks.map((task, index) => {
            const glyph = GLYPH[task.state];
            return (
              <div
                key={task.id}
                className="grid grid-cols-[22px_minmax(0,1fr)_auto] items-baseline gap-x-2.5 gap-y-px border-b border-[var(--color-divider)] py-1.5 text-[13px]"
                data-operator-round-task={task.id}
                data-operator-task-state={task.state}
              >
                <span className={cn('text-center', glyph.className)} aria-label={glyph.label}>
                  {glyph.mark}
                </span>
                <span className={cn('min-w-0', task.state === 'done' ? 'text-muted' : 'text-ink-primary')}>
                  {task.text}
                  {task.ask ? (
                    <span className="ml-1.5 inline-flex align-middle">
                      <ActorChip actor="Engine">{`${task.ask.code} ask`}</ActorChip>
                    </span>
                  ) : null}
                </span>
                <span className={cn('whitespace-nowrap text-[11.5px] tabular-nums', WHEN_TONE[task.state])}>{task.when}</span>
                {index === 0 && group.guide ? (
                  <span className="col-start-2 col-end-4 text-[11.5px] leading-snug text-muted" data-operator-round-guide={group.id}>
                    <Runs runs={group.guide} />
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      ))}
    </GmCard>
  );
}

function RecordSubmitCard({ state, tasks }: { state: SessionState; tasks: readonly OperatorTask[] }) {
  // The round's entries and its submission live in the session (`OperatorRoundProvider`),
  // so they survive a view switch and a persona round trip.
  const { rounds, enter, submit } = useOperatorRounds();
  const model = operatorRecordSubmit(state, tasks, rounds);
  const submitted = model.submitted;
  const round = model.round ?? '';
  const TH = 'whitespace-nowrap border-b border-[var(--color-divider)] px-1.5 py-1.5 text-left text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted';
  const TD = 'border-b border-[var(--color-divider)] px-1.5 py-1.5 align-middle';
  return (
    <GmCard
      title="Record & submit"
      meta={`${round ? `${round} round · ` : ''}${model.rows.length} readings`}
      className="h-full"
      data-operator-record
    >
      <table className="w-full border-collapse text-[12.5px] tabular-nums">
        <thead>
          <tr>
            <th className={TH}>Parameter</th>
            <th className={cn(TH, 'text-right')}>Last</th>
            <th className={TH}>Normal range</th>
            <th className={cn(TH, 'text-right')}>{round}</th>
          </tr>
        </thead>
        <tbody>
          {model.rows.map((row) => (
            <tr key={row.id} className="[&:last-child>td]:border-b-0" data-operator-record-row={row.id}>
              <td className={cn(TD, 'font-medium text-ink-primary')}>{row.parameter}</td>
              <td className={cn(TD, 'whitespace-nowrap text-right', row.off ? 'font-semibold text-[var(--state-warning)]' : 'text-ink-secondary')}>{row.last}</td>
              <td className={cn(TD, 'text-ink-secondary min-[1400px]:whitespace-nowrap')}>
                <span className="whitespace-nowrap">{row.range}</span>
                {row.band ? (
                  <>
                    {' '}
                    <span className={cn(statePillClass('warn'), 'ml-1')}>{row.band}</span>
                  </>
                ) : null}
              </td>
              <td className={cn(TD, 'text-right')}>
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label={`${row.parameter} at ${round}`}
                  value={model.entries[row.id] ?? ''}
                  disabled={submitted !== null || !model.taskId}
                  onChange={(event) => (model.taskId ? enter(model.taskId, row.id, event.target.value) : undefined)}
                  className="h-[26px] w-16 rounded-[7px] border border-[var(--hairline-strong)] bg-[var(--surface-1)] px-1.5 text-right text-[12.5px] tabular-nums text-ink-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2.5 flex items-center justify-end gap-2">
        {submitted ? (
          <span className="text-xs font-semibold text-[var(--state-online)]" data-operator-record-submitted>
            {`Submitted ${submitted}`}
          </span>
        ) : (
          <button
            type="button"
            className={PRIMARY_BUTTON}
            disabled={!model.complete || !model.taskId}
            onClick={() => (model.taskId ? submit(model.taskId) : undefined)}
          >
            {`Submit ${round} round`}
          </button>
        )}
      </div>
    </GmCard>
  );
}

function RoundView({ state, tasks, caption }: { state: SessionState; tasks: readonly OperatorTask[]; caption: string }) {
  return (
    <div className="grid grid-cols-12 gap-3">
      <Commentable name="Primary">
        <div className="col-span-12 lg:col-span-7">
          <RoundSheetCard state={state} tasks={tasks} caption={caption} />
        </div>
      </Commentable>
      <Commentable name="Secondary">
        <div className="col-span-12 lg:col-span-5">
          <RecordSubmitCard state={state} tasks={tasks} />
        </div>
      </Commentable>
    </div>
  );
}

// ---- Guidance ---------------------------------------------------------------------

function GuidanceView({
  state,
  tasks,
  advisoryHref,
  onOpenCase,
}: {
  state: SessionState;
  tasks: readonly OperatorTask[];
  advisoryHref: string;
  onOpenCase: OpenCase;
}) {
  const advisory = useAdvisory();
  const model = useMemo(() => operatorGuidance(state, advisory, tasks), [state, advisory, tasks]);
  return (
    <div className="grid grid-cols-12 gap-3">
      <Commentable name="Primary">
        <div className="col-span-12 lg:col-span-7">
          <GmCard
            title="With me"
            meta={
              <>
                <span>cases routed to CRO and operator</span>
                <Link to={advisoryHref} className="inline-flex items-center gap-0.5 text-[12.5px] font-semibold text-ink-primary hover:underline">
                  Advisory
                  <ArrowUpRight className="size-3" aria-hidden="true" />
                </Link>
              </>
            }
            className="h-full"
            data-operator-with-me
          >
            {model.cards.map((card) => (
              <div key={card.caseId} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 border-b border-[var(--color-divider)] py-[11px] last:border-b-0" data-operator-guidance={card.code}>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-ink-primary">{card.title}</p>
                  <p className="mt-px whitespace-nowrap text-[11px] tracking-[0.04em] text-muted" data-operator-code>{card.meta}</p>
                </div>
                <span className={cn(statePillClass('neutral'), 'self-start')}>{card.state}</span>
                <p className="col-span-2 text-[12.5px] text-ink-secondary">{card.observation}</p>
                <p className="col-span-2 mt-1 rounded-[10px] border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-2.5 py-[7px] text-[12.5px] text-ink-secondary">
                  <span className="font-semibold text-ink-primary">{`${card.ask.lead}:`}</span> {card.ask.text}
                </p>
                <div className="col-span-2 mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted">
                  <ActorChip actor="Human">{card.holder}</ActorChip>
                  {card.escalation ? <span>{card.escalation}</span> : null}
                  <span className="flex-1" />
                  <button type="button" className={GO_LINK} onClick={() => onOpenCase(card.caseId)}>
                    Open case
                    <ArrowUpRight className="size-3" aria-hidden="true" />
                  </button>
                </div>
              </div>
            ))}
            {model.quiet.map((row) => (
              <button
                key={`${row.caseId}-${row.text}`}
                type="button"
                onClick={() => onOpenCase(row.caseId)}
                className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-3 border-t border-[var(--color-divider)] py-[11px] text-left transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-500)]"
                data-operator-quiet-ask={row.code}
              >
                <span className="min-w-0">
                  <span className="block text-[13px] font-medium text-ink-secondary">{`${row.code} · ${row.text}`}</span>
                  <span className="mt-px block text-[11px] tracking-[0.04em] text-muted">{row.meta}</span>
                </span>
                <span className={cn(statePillClass('neutral'), 'self-start')}>{row.state}</span>
              </button>
            ))}
          </GmCard>
        </div>
      </Commentable>
      <Commentable name="Secondary">
        <div className="col-span-12 flex flex-col gap-3 lg:col-span-5">
          {model.closed.length > 0 ? (
            <GmCard title="Closed this shift" data-operator-closed>
              {model.closed.map((row) => (
                <button
                  key={row.caseId}
                  type="button"
                  onClick={() => onOpenCase(row.caseId)}
                  className="grid w-full grid-cols-[48px_minmax(0,1fr)] gap-x-2.5 border-b border-[var(--color-divider)] py-[7px] text-left text-[12.5px] last:border-b-0 hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-500)]"
                  data-operator-closed-row={row.code}
                >
                  <span className="text-xs tabular-nums text-muted">{row.at}</span>
                  <span className="text-ink-secondary">
                    <span data-operator-code className="whitespace-nowrap font-semibold text-ink-primary">{row.code}</span>
                    {` · ${row.text} · `}
                    <span className="whitespace-nowrap text-xs text-muted">{`closed ${row.closed}`}</span>
                  </span>
                </button>
              ))}
            </GmCard>
          ) : null}
          {model.alsoAsked.length > 0 ? (
            <GmCard title="Also asked of me" data-operator-also-asked>
              {model.alsoAsked.map((row) => (
                <button
                  key={row.caseId}
                  type="button"
                  onClick={() => onOpenCase(row.caseId)}
                  className="grid w-full grid-cols-[auto_minmax(0,1fr)] gap-x-2.5 border-b border-[var(--color-divider)] py-[7px] text-left text-[12.5px] last:border-b-0 hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-500)]"
                  data-operator-also-row={row.code}
                >
                  <span data-operator-code className="whitespace-nowrap text-xs tracking-[0.04em] text-muted">{row.code}</span>
                  <span className="text-ink-secondary">
                    <span className="font-semibold text-ink-primary">{`${row.count} ${row.kind}`}</span>
                    {` · ${row.items.join(' · ')}`}
                  </span>
                </button>
              ))}
            </GmCard>
          ) : null}
        </div>
      </Commentable>
    </div>
  );
}

// ---- Safety -----------------------------------------------------------------------

const ALERT_TONE: Record<'high' | 'med' | 'low', 'bad' | 'warn' | 'neutral'> = { high: 'bad', med: 'warn', low: 'neutral' };

function SafetyView() {
  const alerts = operatorAlerts();
  const contacts = operatorContacts();
  return (
    <div className="grid grid-cols-12 gap-3">
      <Commentable name="Primary">
        <div className="col-span-12 lg:col-span-7">
          <GmCard title="Safety alerts" meta="this shift" data-operator-safety>
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-baseline gap-x-2.5 border-b border-[var(--color-divider)] py-[9px] text-[13px] last:border-b-0"
                data-operator-alert={alert.id}
              >
                <span className={statePillClass(ALERT_TONE[alert.severity])}>{alert.label}</span>
                <span className="font-medium text-ink-primary">{alert.text}</span>
                <span className="text-xs tabular-nums text-muted">{alert.time}</span>
              </div>
            ))}
          </GmCard>
        </div>
      </Commentable>
      {contacts ? (
        <Commentable name="Secondary">
          <div className="col-span-12 lg:col-span-5">
            <GmCard title={contacts.title} data-operator-contacts>
              <dl className="grid grid-cols-2 gap-x-[18px] gap-y-2.5 tabular-nums">
                {contacts.rows.map((row) => (
                  <div key={row.role} className="flex min-w-0 flex-col">
                    <dt className={CAPS}>{row.role}</dt>
                    <dd className="text-[13px] font-semibold text-ink-primary">{row.contact}</dd>
                  </div>
                ))}
              </dl>
            </GmCard>
          </div>
        </Commentable>
      ) : null}
    </div>
  );
}

// ---- Composition ------------------------------------------------------------------

/**
 * The Operator's landing (polish.P16 #703, locked comp `landing-operator.html`
 * A with B's round sheet by area and C's closed-this-shift list): the landing
 * family at circuit scope. Four strip cells are the tab bar — Area status (the
 * default), Round sheet, Guidance and Safety — beside the Process card; the
 * default view reads the seven areas against their bands beside the operator's
 * next tasks, in the order to do them. No money reaches this persona.
 */
export function OperatorLanding({
  state,
  projectSlug,
  onOpenCase,
}: {
  state: SessionState;
  projectSlug: string;
  onOpenCase: OpenCase;
}) {
  const [tab, setTab] = useState<OperatorTabId>('area');
  const advisory = useAdvisory();
  const { rounds } = useOperatorRounds();
  const tasks = useMemo(() => operatorTasks(state, advisory, rounds), [state, advisory, rounds]);
  const plantHref = projectSidebarHref(projectSlug, 'plant-model');
  const caption = operatorRoundCaption();
  return (
    <div className="space-y-3" data-operator-landing>
      <p className="-mt-3 text-sm text-muted" data-operator-subtitle>{operatorSubtitle(state)}</p>
      <OperatorLeadBand
        state={state}
        tab={tab}
        onTabChange={setTab}
        processHref={projectSidebarHref(projectSlug, 'process')}
      />
      {/* Each view anchors its two columns at the Body level (Primary,
          Secondary), the paths `anchors.ts` registers. */}
      <div id="operator-landing-panel" role="tabpanel" aria-labelledby={`op-tab-${tab}`}>
        {tab === 'area' ? <AreaView state={state} tasks={tasks} plantHref={plantHref} onGoto={setTab} /> : null}
        {tab === 'round' ? <RoundView state={state} tasks={tasks} caption={caption} /> : null}
        {tab === 'guidance' ? (
          <GuidanceView state={state} tasks={tasks} advisoryHref={projectSidebarHref(projectSlug, 'advisory')} onOpenCase={onOpenCase} />
        ) : null}
        {tab === 'safety' ? <SafetyView /> : null}
      </div>
    </div>
  );
}
